import { TaskFilterDto, TasksListDto } from '@/app/Data/DTO/TasksListDto';
import { AppState } from '@/app/State/AppState';
import { selectTasksActiveStatus, selectTasksFilter, selectTasksIsFetching } from './../../State/Tasks/selector';
import { GetAllTasksAction, GetTasksAction, SetActiveTasksStatusAction } from '@/app/State/Tasks/action';
import { selectTenantProfile } from '@/app/State/Tenant/tenant.selector';
import { IMessagesRepository } from '@/app/core/IRepositories/IMessagesRepository';
import { ITasksRepository } from '@/app/core/IRepositories/ITasksRepository';
import { IUsersRepository } from '@/app/core/IRepositories/IUsersRepository';
import { Task, TaskDetail, TaskFilter, TaskStatus } from '@/app/core/Models/Task';
import { TeamMember } from '@/app/core/Models/TeamMember';
import { GetTasksByChannelIdUseCase } from '@/app/core/usecases/Tasks/GetTasksByChannelIdUseCase';
import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable, Subject, catchError, combineLatest, distinctUntilChanged, filter, firstValueFrom, map, of, shareReplay, startWith, switchMap, take, tap, withLatestFrom, BehaviorSubject } from 'rxjs';
import { selectInboxActiveThreadId } from '@/app/State/Inbox/inbox.selector';
import { RecentInteraction } from '@/app/core/Models/Interaction';
import { TaskFormComponent } from '../tasks/task-form/task-form.component';
import { MatDialog } from '@angular/material/dialog';
import { UsersViewModel } from './usersViewModel';
import { FormControl } from '@angular/forms';
import { User } from '@/app/core/Models/User';
import { Tag } from '@/app/core/Models/tag';


@Injectable({
  providedIn: 'root',
})
export class TasksViewModel {
  readonly profile$ = this.store.select(selectTenantProfile);
  readonly team$ = this.usersRepository.getLocalTeamMembers().pipe(
    shareReplay({ bufferSize: 1, refCount: true })
  )
  refreshTasksByChannelId$ = new Subject<void>();
  isFetching$ = this.store.select(selectTasksIsFetching);
  tasksFilter$ = this.store.select(selectTasksFilter);
  TasksActiveStatus$ = this.store.select(selectTasksActiveStatus);

  appliedFilter = false;
  pagination = { page: 1, page_size: 50 };
  totalTasks: number;
  private _statusCount: { open: number, 'in-progress': number, done: number, trash: number };
  
  statusCount$ = new BehaviorSubject<{ open: number, 'in-progress': number, done: number, trash: number }>({ open: 0, 'in-progress': 0, done: 0, trash: 0 });
  
  // Add last updated tracking
  lastUpdated$ = new BehaviorSubject<Date | null>(null);

  set statusCount(value: { open: number, 'in-progress': number, done: number, trash: number }) {
    this._statusCount = value;
    this.statusCount$.next(value);
  }

  get statusCount() {
    return this._statusCount;
  }

  // Add method to update last updated timestamp
  updateLastUpdated(): void {
    this.lastUpdated$.next(new Date());
  }

  // Add refresh method
  refreshTasks(filter?: TaskFilterDto): void {
    this.getTasksv2(filter);
  }

  constructor(
    private store: Store<AppState>,
    private matDialog: MatDialog,
    private tasksRepository: ITasksRepository,
    private usersRepository: IUsersRepository,
    private messagesRepository: IMessagesRepository,
    private getTasksByChannelIdUseCase: GetTasksByChannelIdUseCase,
    private userViewModel: UsersViewModel
  ) { }

  activeChannelTasks$ = combineLatest([
    this.store.select(selectInboxActiveThreadId).pipe(
      switchMap((threadId) => threadId ? this.messagesRepository.findRecentMessage(threadId) : of(undefined)),
      filter((recentInteraction): recentInteraction is RecentInteraction => !!recentInteraction),
      distinctUntilChanged((a, b) => a?.channel_id == b?.channel_id)
    ),
    this.refreshTasksByChannelId$.pipe(startWith(true))
  ]).pipe(
    switchMap(([{ channel_id }]) => this.getTasksByChannelId(channel_id)),
    shareReplay({ bufferSize: 1, refCount: true })
  )
  openedTasks$ = this.activeChannelTasks$.pipe(
    map(tasks => tasks.filter(({ status }) => [TaskStatus.inProgress, TaskStatus.open].includes(status)).length),
  )

  tasks$: Observable<Record<TaskStatus, Task[]>> = this.tasksRepository
    .getLocalTasksV2()
    .pipe(
      startWith([]),
      distinctUntilChanged(),
      withLatestFrom(this.team$),
      map(([tasks, team]: any) => this.mapTaskUser(tasks, team)),
      map(tasks => ({
        'open': tasks.filter(t => t.status === 'open'),
        'done': tasks.filter(t => t.status === 'done'),
        'in-progress': tasks.filter(t => t.status === 'in-progress'),
        'trash': tasks.filter(t => t.status === 'trash'),
      })),
      shareReplay({ refCount: true, bufferSize: 1 })
    );

  getTasksByChannelId(
    channelId: number,
    status: TaskStatus = TaskStatus.open,
    filter: TaskFilter = TaskFilter.All
  ): Observable<Task[]> {
    return this.getTasksByChannelIdUseCase.execute({ channelId, filter, status }).pipe(
      withLatestFrom(this.team$),
      map(([tasks, team]) => this.mapTaskUser(tasks, team)),
      catchError(() => of([]))
    );
  }

  getAllTasks(): void {
    this.profile$.pipe(
      take(1),
      withLatestFrom(this.TasksActiveStatus$)
    ).subscribe(([profile, status]) => {
      const request: TasksListDto = {
        filter: TaskFilter.All,
        userId: profile?.userId,
        status
      };
      this.store.dispatch(GetAllTasksAction({ payload: request }));
    });
  }

  getTasksv2(filter?: TaskFilterDto): void {
    let new_payload = { ...filter, page: this.pagination.page, page_size: this.pagination.page_size };
    this.store.dispatch(GetTasksAction({ payload: new_payload as TaskFilterDto }));
  }

  getMyTasksV2(filter?: TaskFilterDto): void {
    this.store.dispatch(GetTasksAction({ payload: filter as TaskFilterDto }));
  }

  resetFilterPagination(): void {
    this.pagination.page = 1;
    this.pagination.page_size = 50;
  }

  getTasksByStatus(status: TaskStatus[]): void {
    this.profile$.pipe(
      take(1),
      withLatestFrom(this.TasksActiveStatus$)
    ).subscribe(([profile]) => {
      const request: TasksListDto = {
        filter: TaskFilter.All,
        userId: profile?.userId,
        status
      };
      this.store.dispatch(GetAllTasksAction({ payload: request }));
    });
  }

  getMyTasks(): void {
    this.profile$.pipe(
      take(1),
      withLatestFrom(this.TasksActiveStatus$)
    ).subscribe(([profile, status]) => {
      const request: TasksListDto = {
        filter: TaskFilter.Mine,
        userId: profile?.userId,
        status
      };
      this.store.dispatch(GetAllTasksAction({ payload: request }));
    });
  }

  tasksActiveStatusChange(status: TaskStatus[]) {
    this.store.dispatch(SetActiveTasksStatusAction({ payload: status }))
  }

  mapTaskUser = (tasks: Task[], team: TeamMember[]): Task[] =>
    tasks.map((task) => {
      const user = team.find(u => u.user_id == task.assigned_to)
      if (user) {
        task.assignedUser = user;
        task.userAvatar = user.avatar;
        task.assignedToName = `${user.given_name} ${user.family_name}`;
      }
      return task;
    })

  openNewTask(data?: { channel_id: number, inbox_id?: number }): void {
    this.matDialog.open<any, any, { type: 'archive' | 'updated' | 'created', data?: TaskDetail }>(TaskFormComponent, {
      disableClose: true,
      height: 'fit-content',
      maxHeight: '90vh',
      width: '700px',
      maxWidth: '90vw',
      panelClass: 'custom-dialog-container',
      data
    })
      .afterClosed()
      .subscribe((res) => {
        res?.data?.interaction_id ? this.refreshTasksByChannelId$.next()
          : res?.data ? this.getTasksv2()
            : undefined
      });
  }

  saveTask(task: Task): void {
    this.tasksRepository.saveTask(task);
  }

  getLocalTaskById(taskId: number) {
    return this.tasksRepository.getLocalTaskById(taskId);
  }

  getTaskAssigneesByUserId(assignees: string[]) {

    return new Promise(async (resolve) => {
      const users = await firstValueFrom(this.userViewModel.usersList$);
      const task_assignees: Array<{ avatar: string, family_name: string, given_name: string, user_id: any }> = [];
      assignees.forEach((id) => {
        users.forEach((user: any) => {
          if (user.user_id === id) {
            task_assignees.push({ avatar: user.avatar, family_name: user.family_name, given_name: user.given_name, user_id: user.user_id })
          }
        })
      })
      resolve(task_assignees);
    });
  }

  getTaskStatusIcon(status: "open" | "in-progress" | "done" | "archive" | "trash") {
    switch (status) {
      case "open":
        return "assets/icons/new-set/task-status/upcoming.svg";
      case "in-progress":
        return "assets/icons/task/task-status/inProgress.svg";
      case "done":
        return "assets/icons/task/task-status/done.svg"
      case "archive":
      case "trash":
        return "assets/icons/new-set/task-status/archived.svg"
    }
  }

  getTaskPriorityIcon(priority: "low" | "medium" | "high" | "none") {
    switch (priority) {
      case "low":
        return "assets/icons/new-set/low.svg";
      case "medium":
        return "assets/icons/new-set/medium.svg";
      case "high":
        return "assets/icons/new-set/high.svg";
      case "none":
        return "assets/icons/new-set/circle-off.svg";
    }
  }

  createTaskFilterViewPayload(assigneesValue: FormControl, prioritiesValue: FormControl, tagsValue: FormControl, dueDate: FormControl) {
    let payload = [];
    if (assigneesValue.value.length) {
      payload.push({
        module: 'tasks',
        category: 'Assignees',
        values: assigneesValue.value.map((item: User) => { return { 'user_id': item.user_id } })
      });
    }
    if (prioritiesValue.value.length) {
      payload.push({
        module: 'tasks',
        category: 'Task Priority',
        values: prioritiesValue.value.map((item: string) => { return { 'task_priority': item } })
      });
    }
    if (tagsValue.value.length) {
      payload.push({
        module: 'tasks',
        category: 'Tags',
        values: tagsValue.value.map((item: Tag) => { return { 'tag_id': item.tag_id } })
      });
    }
    if (dueDate.value) {
      payload.push({
        module: 'tasks',
        category: 'Due Date',
        values: [{ 'deadline': dueDate.value }]
      });
    }

    return payload;
  }

  loadMoreTasks(filter: TaskFilterDto) {
    return new Promise(async (resolve) => {
      const tasks = await firstValueFrom(this.tasksRepository.getTasksV2(filter));
      this.tasksRepository.appendTasks(tasks.results);
      this.updateLastUpdated();
      return resolve(tasks.results);
    });
  }

}
